import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Paginator } from "primereact/paginator";
import "primereact/resources/themes/lara-light-blue/theme.css";

import Layout from "../component/layout";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { getListOfFacilities } from "../../../utils/urls";
import { setItem, getItem } from "../../../utils/localStorage";
import { useFacilityStore } from "../../../app/z-store/store";

function Facilities() {
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(12);
  const setFacilityId = useFacilityStore((state) => state.setFacilityId);

  // Filter by name or location as the user types
  const filteredFacilities = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return facilities;
    return facilities.filter(
      (f) =>
        f.name?.toLowerCase().includes(term) ||
        f.location?.toLowerCase().includes(term)
    );
  }, [facilities, searchTerm]);

  const paginatedFacilities = filteredFacilities.slice(first, first + rows);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setFirst(0);
  };

  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const getFacilities = async () => {
    try {
      const response = await makeRequest2(getListOfFacilities, "GET", {});

      console.log("Facilities response:", response);

      if (response.success) {
        const currentUserValue = await getItem("APPUSER");

        let currentUser;
        // Check if currentUserValue is a string or an object
        if (typeof currentUserValue === "string") {
          try {
            currentUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
            return toastify("Invalid user data.", "error");
          }
        } else {
          currentUser = currentUserValue; // Assume it's already parsed
        }

        const role = currentUser?.user?.role;
        if (role !== "guard") {
          setFacilities(response.data);
        } else {
          const facilityId = currentUser?.user?.facilityId;
          const filter = response.data.filter((x) => x._id === facilityId);
          setFacilities(filter);
        }
      }
    } catch (err) {
      console.error("Error in getFacilities:", err.message);
      toastify("Server error.", "error");
    }
  };

  // const getFacilities = async () => {
  //   try {
  //     const response = await makeRequest2(getListOfFacilities, "GET", {});
  //     console.log("Facilities response:", response);

  //     if (response.success) {
  //       const currentUserValue = await getItem("APPUSER");
  //       let currentUser;

  //       // Check if currentUserValue is a string or an object
  //       if (typeof currentUserValue === "string") {
  //         try {
  //           currentUser = JSON.parse(currentUserValue);
  //         } catch (err) {
  //           console.error("Error parsing APPUSER:", err);
  //           return toastify("Invalid user data.", "error");
  //         }
  //       } else {
  //         currentUser = currentUserValue; // Assume it's already parsed
  //       }

  //       const role = currentUser?.user?.role;
  //       const facilityId = currentUser?.user?.facilityId;
  //       const type = currentUser?.user?.type;

  //       if (role === "admin" && type === "Company") {
  //         setFacilities(response.data);
  //       } else if (role === "Staff" || role === "guard") {
  //         const filter = response.data.filter((x) => x._id === facilityId);
  //         setFacilities(filter);
  //       } else {
  //         // Default: show only assigned facility for any other role
  //         const filter = response.data.filter((x) => x._id === facilityId);
  //         setFacilities(filter);
  //       }
  //     }
  //   } catch (err) {
  //     console.error("Error in getFacilities:", err.message);
  //     toastify("Server error.", "error");
  //   }
  // };

  const handleFacilityClick = async (facility) => {
    try {
      await setItem("selectedFacilityId", facility._id);
      await setItem("selectedFacility", facility);
      await setItem("selectedFacilityName", facility.name);
      setFacilityId(facility._id);

      // ✅ Store the logo with the full URL
      if (facility.logo) {
        await setItem("selectedFacilityLogo", facility.logo);
      }

      setFacilityId(facility._id);

      const currentUserValue = await getItem("APPUSER");
      let currentUser;

      if (typeof currentUserValue === "string") {
        try {
          currentUser = JSON.parse(currentUserValue);
        } catch (err) {
          console.error("Error parsing APPUSER:", err);
          return toastify("Invalid user data.", "error");
        }
      } else {
        currentUser = currentUserValue;
      }

      const role = currentUser?.user?.role;

      if (role !== "guard") {
        if (!facility.isOnboarded) {
          navigate("/facility/onboarding", {
            state: { facility }
          });
        } else {
          navigate("/facility/", {
            state: { facility }
          });
        }
      } else if (role === "guard") {
        navigate("/facility/visitor_access_management/home");
      }
    } catch (err) {
      console.error("Error in handleFacilityClick:", err.message);
      toastify("An error occurred.", "error");
    }
  };

  useEffect(() => {
    getFacilities();
  }, []);

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={"#"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Facilities</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="ti ti-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search facilities by name or location..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
          </div>
          <div className="row">
            {filteredFacilities.length === 0 ? (
              <div className="col-md-12 text-center">
                <p>
                  {searchTerm
                    ? `No facilities match "${searchTerm}"`
                    : "No facilities available"}
                </p>
              </div>
            ) : (
              paginatedFacilities.map((facility, index) => (
                <div className="col-md-3" key={facility._id || index}>
                  <div className="pc-component">
                    <div
                      className="card mb-2 text-center"
                      style={{ position: "relative" }}
                    >
                      <span
                        className="badge bg-warning"
                        style={{ position: "absolute", top: 5, left: 5 }}
                      >
                        {first + index + 1}
                      </span>
                      <span className="badge bg-light-primary">
                        {" "}
                        <i className="ti ti-home f-50 mt-3"></i>
                      </span>

                      <div
                        className="card-body"
                        onClick={() => handleFacilityClick(facility)}
                        style={{ cursor: "pointer" }}
                      >
                        <h5 className="card-title">{facility.name}</h5>
                        <p className="card-text">
                          <span className="badge bg-light-primary">
                            {facility.location}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {filteredFacilities.length > 0 && (
            <Paginator
              first={first}
              rows={rows}
              totalRecords={filteredFacilities.length}
              rowsPerPageOptions={[12, 24, 48]}
              onPageChange={onPageChange}
              template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} facilities"
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Facilities;
